import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { navigate, useLocation, useToast, useTRPC } from '@barev/web';
import { useEffect, useMemo, useState } from 'react';
import type { Me } from '../App.tsx';
import { errText, useT } from '../i18n.tsx';
import { Avatar, Dialog, download, Field, I, PlanBadge, Skeleton, sourceLabel } from '../components/kit.tsx';
import { BroadcastDialog } from './Admin5.tsx';

type Plan = 'any' | 'none' | 'BAREV_PLUS' | 'ELITE';
interface Filters {
  search?: string;
  plan?: Plan;
  subSource?: 'PAID' | 'MANUAL' | 'AGENCY_GIFT';
  source?: string;
  agencyId?: string;
  tag?: string;
  expiringDays?: number;
  segment?: 'MOVING' | 'LIVING';
  paying?: 'yes' | 'no';
}
type Sort = 'new' | 'revenue' | 'ends';

const FILTER_KEYS = ['search', 'plan', 'subSource', 'source', 'agencyId', 'tag', 'segment', 'paying'] as const;

function readQuery(q: URLSearchParams): { f: Filters; sort: Sort; page: number } {
  const f: Filters = {};
  for (const k of FILTER_KEYS) {
    const v = q.get(k);
    if (v) (f as Record<string, string>)[k] = v;
  }
  const exp = Number(q.get('expiring'));
  if (exp > 0) f.expiringDays = exp;
  return { f, sort: (q.get('sort') as Sort) || 'new', page: Number(q.get('page')) || 0 };
}

function writeQuery(f: Filters, sort: Sort, page: number) {
  const q = new URLSearchParams();
  for (const k of FILTER_KEYS) if (f[k]) q.set(k, String(f[k]));
  if (f.expiringDays) q.set('expiring', String(f.expiringDays));
  if (sort !== 'new') q.set('sort', sort);
  if (page) q.set('page', String(page));
  const s = q.toString();
  navigate(`/clients${s ? `?${s}` : ''}`, { replace: true });
}

export function Clients({ me }: { me: Me }) {
  const { t, amd, date } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const { query } = useLocation();
  const { f, sort, page } = useMemo(() => readQuery(query), [query]);
  const isSuper = me.roles.includes('SUPERADMIN');

  const [search, setSearch] = useState(f.search ?? '');
  useEffect(() => {
    const id = setTimeout(() => {
      if ((f.search ?? '') !== search) writeQuery({ ...f, search: search || undefined }, sort, 0);
    }, 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const set = (patch: Partial<Filters>) => writeQuery({ ...f, ...patch }, sort, 0);
  const activeCount = Object.entries(f).filter(([k, v]) => k !== 'search' && v !== undefined).length;
  const [showFilters, setShowFilters] = useState(activeCount > 1 || !!(f.source || f.agencyId || f.tag || f.segment || f.subSource));

  const list = useQuery({ ...trpc.bo.clients.list.queryOptions({ ...f, sort, page }), placeholderData: keepPreviousData });
  const facets = useQuery(trpc.bo.clients.facets.queryOptions());
  const segments = useQuery(trpc.bo.segments.list.queryOptions());
  const removeSeg = useMutation(trpc.bo.segments.remove.mutationOptions({ onSuccess: () => qc.invalidateQueries({ queryKey: trpc.bo.segments.list.queryKey() }) }));
  const exportCsv = useMutation(
    trpc.bo.clients.exportCsv.mutationOptions({
      onSuccess: (r) => {
        download(r.filename, r.content);
        toast(t('clients.exported'));
      },
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  const [saveOpen, setSaveOpen] = useState(false);
  const [broadcast, setBroadcast] = useState(false);
  const canBroadcast = isSuper || me.roles.includes('SUPPORT');

  const data = list.data;
  const from = data ? page * data.pageSize + 1 : 0;
  const to = data ? Math.min(data.total, (page + 1) * data.pageSize) : 0;

  return (
    <>
      <div className="bo-head">
        <div>
          <h1 className="bo-title">{t('clients.title')}</h1>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {canBroadcast && (
            <>
              <a className="bo-btn bo-btn--ghost" href="#/broadcasts">
                {t('bc.historyBtn')}
              </a>
              <button type="button" className="bo-btn bo-btn--ghost" onClick={() => setBroadcast(true)}>
                <I name="send" size={16} />
                {t('clients.broadcast')}
              </button>
            </>
          )}
          <button type="button" className="bo-btn bo-btn--ghost" onClick={() => exportCsv.mutate(f)} disabled={exportCsv.isPending}>
            <I name="download" size={16} />
            {t('clients.export')}
          </button>
        </div>
      </div>

      <div className="bo-toolbar">
        <label className="bo-search">
          <I name="search" size={18} />
          <span className="bo-visually-hidden">{t('clients.search')}</span>
          <input className="bo-input" type="search" value={search} placeholder={t('clients.search')} onChange={(e) => setSearch(e.target.value)} />
        </label>
        <div className="bo-pills" role="group" aria-label={t('filter.plan')}>
          {(
            [
              [undefined, t('filter.all')],
              ['any', t('filter.any')],
              ['BAREV_PLUS', t('plan.BAREV_PLUS')],
              ['ELITE', t('plan.ELITE')],
              ['none', t('filter.none')],
            ] as [Plan | undefined, string][]
          ).map(([v, label]) => (
            <button key={label} type="button" className="bo-pill" aria-pressed={f.plan === v} onClick={() => set({ plan: v })}>
              {label}
            </button>
          ))}
          <button type="button" className="bo-pill" aria-pressed={!!f.expiringDays} onClick={() => set({ expiringDays: f.expiringDays ? undefined : 7 })}>
            {t('filter.expiring')}
          </button>
          <button type="button" className="bo-pill" aria-pressed={showFilters} onClick={() => setShowFilters((s) => !s)} aria-expanded={showFilters}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <I name="filter" size={15} />
              {t('filter.more')}
            </span>
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="bo-filters">
          <Field label={t('filter.subSource')}>
            <select className="bo-select" value={f.subSource ?? ''} onChange={(e) => set({ subSource: (e.target.value || undefined) as Filters['subSource'] })}>
              <option value="">{t('filter.anyValue')}</option>
              {(['PAID', 'MANUAL', 'AGENCY_GIFT'] as const).map((s) => (
                <option key={s} value={s}>
                  {t(`subSource.${s}`)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('filter.source')}>
            <select className="bo-select" value={f.source ?? ''} onChange={(e) => set({ source: e.target.value || undefined })}>
              <option value="">{t('filter.anyValue')}</option>
              {(facets.data?.sources ?? []).map((s) => (
                <option key={s} value={s}>
                  {sourceLabel(t, { source: s })}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('filter.agency')}>
            <select className="bo-select" value={f.agencyId ?? ''} onChange={(e) => set({ agencyId: e.target.value || undefined })}>
              <option value="">{t('filter.anyValue')}</option>
              {(facets.data?.agencies ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('filter.tag')}>
            <select className="bo-select" value={f.tag ?? ''} onChange={(e) => set({ tag: e.target.value || undefined })}>
              <option value="">{t('filter.anyValue')}</option>
              {(facets.data?.tags ?? []).map((tg) => (
                <option key={tg.tag} value={tg.tag}>
                  {tg.tag} ({tg.count})
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('filter.segment')}>
            <select className="bo-select" value={f.segment ?? ''} onChange={(e) => set({ segment: (e.target.value || undefined) as Filters['segment'] })}>
              <option value="">{t('filter.anyValue')}</option>
              <option value="MOVING">{t('segment.MOVING')}</option>
              <option value="LIVING">{t('segment.LIVING')}</option>
            </select>
          </Field>
          <Field label={t('filter.paying')}>
            <select className="bo-select" value={f.paying ?? ''} onChange={(e) => set({ paying: (e.target.value || undefined) as Filters['paying'] })}>
              <option value="">{t('filter.anyValue')}</option>
              <option value="yes">{t('filter.payingYes')}</option>
              <option value="no">{t('filter.payingNo')}</option>
            </select>
          </Field>
          <Field label={t('sort.label')}>
            <select className="bo-select" value={sort} onChange={(e) => writeQuery(f, e.target.value as Sort, 0)}>
              <option value="new">{t('sort.new')}</option>
              {isSuper && <option value="revenue">{t('sort.revenue')}</option>}
              <option value="ends">{t('sort.ends')}</option>
            </select>
          </Field>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="bo-btn bo-btn--soft bo-btn--sm" style={{ background: '#fff' }} onClick={() => setSaveOpen(true)} disabled={activeCount === 0 && !f.search}>
              {t('segments.save')}
            </button>
            <button
              type="button"
              className="bo-btn bo-btn--ghost bo-btn--sm"
              onClick={() => {
                setSearch('');
                writeQuery({}, 'new', 0);
              }}
            >
              {t('filter.reset')}
            </button>
          </div>
        </div>
      )}

      {!!segments.data?.length && (
        <div className="bo-segments" aria-label={t('segments.title')}>
          <span className="bo-small bo-muted" style={{ marginRight: 4 }}>
            {t('segments.title')}
          </span>
          {segments.data.map((s) => (
            <span key={s.id} className="bo-seg">
              <button
                type="button"
                onClick={() => {
                  const sf = s.filters as Filters;
                  setSearch(sf.search ?? '');
                  writeQuery(sf, sort, 0);
                }}
              >
                {s.name}
              </button>
              <button type="button" aria-label={t('segments.remove', { name: s.name })} onClick={() => removeSeg.mutate({ id: s.id })}>
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {data && (
        <div className="bo-summary">
          <span>{t('clients.found', { n: data.total })}</span>
          {data.totalRevenue != null && <span>{t('clients.revenueTotal', { sum: amd(data.totalRevenue) })}</span>}
        </div>
      )}

      {!data ? (
        <Skeleton h={420} />
      ) : data.items.length === 0 ? (
        <div className="bo-panel bo-empty">{activeCount || f.search ? t('clients.empty') : t('clients.emptyAll')}</div>
      ) : (
        <>
          <table className="bo-table" style={{ opacity: list.isPlaceholderData ? 0.6 : 1 }}>
            <thead>
              <tr>
                <th>{t('col.client')}</th>
                <th>{t('col.sub')}</th>
                <th>{t('col.source')}</th>
                <th>{t('col.since')}</th>
                {isSuper && <th className="bo-r">{t('col.revenue')}</th>}
              </tr>
            </thead>
            <tbody>
              {data.items.map((c) => (
                <tr key={c.id} onClick={() => navigate(`/clients/${c.id}`)}>
                  <td>
                    <a href={`#/clients/${c.id}`} className="bo-who" style={{ textDecoration: 'none', color: 'inherit' }} onClick={(e) => e.stopPropagation()}>
                      <Avatar name={c.name} id={c.id} />
                      <span style={{ minWidth: 0 }}>
                        <span className="bo-who__name">{c.name}</span>
                        <span className="bo-who__meta" style={{ display: 'block' }}>
                          {[c.username && `@${c.username}`, c.phone].filter(Boolean).join(', ') || '—'}
                        </span>
                        {c.tags.length > 0 && (
                          <span className="bo-tags" style={{ marginTop: 4 }}>
                            {c.tags.map((tg) => (
                              <span key={tg} className="bo-tag">
                                {tg}
                              </span>
                            ))}
                          </span>
                        )}
                      </span>
                    </a>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <PlanBadge plan={c.sub?.plan ?? null} t={t} />
                      {c.sub && c.sub.source !== 'PAID' && <I name="gift" size={15} />}
                    </div>
                    {c.sub && <div className="bo-who__meta">{t('card.until', { date: date(c.sub.endsAt) })}</div>}
                  </td>
                  <td className="bo-small">{sourceLabel(t, c)}</td>
                  <td className="bo-small bo-muted">{date(c.createdAt)}</td>
                  {isSuper && <td className="bo-r bo-num">{c.revenue ? amd(c.revenue) : <span className="bo-faint">0</span>}</td>}
                </tr>
              ))}
            </tbody>
          </table>

          <div className="bo-cards">
            {data.items.map((c) => (
              <a key={c.id} className="bo-ccard" href={`#/clients/${c.id}`}>
                <div className="bo-who">
                  <Avatar name={c.name} id={c.id} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="bo-who__name">{c.name}</div>
                    <div className="bo-who__meta">{sourceLabel(t, c)}</div>
                  </div>
                  {isSuper && c.revenue != null && <b className="bo-num">{amd(c.revenue)}</b>}
                </div>
                <div className="bo-ccard__row">
                  <PlanBadge plan={c.sub?.plan ?? null} t={t} />
                  {c.sub && <span>{t('card.until', { date: date(c.sub.endsAt) })}</span>}
                  {c.tags.map((tg) => (
                    <span key={tg} className="bo-tag">
                      {tg}
                    </span>
                  ))}
                </div>
              </a>
            ))}
          </div>

          {data.total > data.pageSize && (
            <div className="bo-pager">
              <button type="button" className="bo-btn bo-btn--ghost bo-btn--sm" disabled={page === 0} onClick={() => writeQuery(f, sort, page - 1)}>
                {t('clients.prev')}
              </button>
              <span className="bo-small bo-muted">{t('clients.page', { from, to, total: data.total })}</span>
              <button type="button" className="bo-btn bo-btn--ghost bo-btn--sm" disabled={to >= data.total} onClick={() => writeQuery(f, sort, page + 1)}>
                {t('clients.next')}
              </button>
            </div>
          )}
        </>
      )}

      <SaveSegment open={saveOpen} onClose={() => setSaveOpen(false)} filters={f} />
      {broadcast && <BroadcastDialog filters={f as Record<string, unknown>} onClose={() => setBroadcast(false)} />}
    </>
  );
}

function SaveSegment({ open, onClose, filters }: { open: boolean; onClose: () => void; filters: Filters }) {
  const { t } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState('');
  const save = useMutation(
    trpc.bo.segments.save.mutationOptions({
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: trpc.bo.segments.list.queryKey() });
        toast(t('segments.saved'));
        setName('');
        onClose();
      },
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('segments.save')}
      footer={
        <>
          <button type="button" className="bo-btn bo-btn--ghost" onClick={onClose}>
            {t('ui.close')}
          </button>
          <button type="button" className="bo-btn" disabled={!name.trim() || save.isPending} onClick={() => save.mutate({ name: name.trim(), filters })}>
            {t('ui.save')}
          </button>
        </>
      }
    >
      <Field label={t('segments.name')}>
        <input className="bo-input" value={name} onChange={(e) => setName(e.target.value)} autoFocus maxLength={60} />
      </Field>
    </Dialog>
  );
}
